import {css} from 'aphrodite';
import React, {Dispatch, SetStateAction, useState} from 'react';

import {ResidueType} from 'biojs/BioConstants';
import {getDefaultSearchSearchParams} from 'nav/sidepanel/listing/SearchSearchParams';
import {StepKey} from 'search/blast/BLASTModal';
import {
  BLASTInputSource,
  BlastModalTrigger,
  trackBlastModalClose,
  trackBlastSubmission,
} from 'search/blast/BLASTTracking';
import {
  getSelectBlastTypeStepTitle,
  getSelectSequencesStepTitle,
  mergeBLASTFilter,
  stripWhitespace,
} from 'search/blast/BLASTUtil';
import {BLAST_MODAL_TITLE, styles} from 'search/blast/constants';
import {getBLASTParamsFromFilter} from 'search/blast/parameters/uiParamHelpers';
import SelectBlastTypePane from 'search/blast/SelectBlastTypePane';
import SetParametersPane from 'search/blast/SetParametersPane';
import {validateBLASTSequence} from 'search/blast/validation';
import {BLASTFilter, BLASTOperator, SearchParams, SortKey} from 'search/SearchConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import TextArea from 'ui-library/bs/TextArea';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import TextInput from 'ui-library/inputs/TextInput';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavSize} from 'ui-library/StepNav';

export interface BLASTModalForSelectionProps {
  initialFilter: BLASTFilter;
  residueSourceId: string;
  residueType: ResidueType;
  selectedResidues: string;
  displayStart: number;
  displayEnd: number;
  displayLength: number;
  onRequestSubmit: (searchParams: SearchParams) => void;
  onRequestClose: () => void;
}

const sequenceTypeMap: {[residueType in ResidueType]: string} = {
  [ResidueType.NUCLEOTIDE]: 'Nucleotide',
  [ResidueType.AA]: 'Amino acid',
};

function getStepModalButtons(
  step: StepKey,
  setCurrentStepKey: Dispatch<SetStateAction<StepKey>>,
  selectedResidues: string,
  filter: BLASTFilter,
  setFilter: Dispatch<SetStateAction<BLASTFilter>>,
  onRequestSubmit: (searchParams: SearchParams) => void
): Array<ModalButton> {
  if (step === StepKey.SelectBlastType) {
    return [
      {
        text: 'Cancel',
        cancel: true,
        dataTestAttrs: {component: 'SequenceMapBlastModal', element: 'cancel'},
      },
      {
        text: 'Next',
        submit: true,
        onClick: () => {
          setCurrentStepKey(StepKey.SelectSequences);
          // add the default params in now that the blast type is chosen
          setFilter((previousFilter) => ({
            ...previousFilter,
            params: getBLASTParamsFromFilter(filter),
          }));
        },
        dataTestAttrs: {component: 'SequenceMapBlastModal', element: 'next'},
      },
    ];
  } else if (step === StepKey.SelectSequences) {
    return [
      {
        text: 'Back',
        onClick: () => {
          setCurrentStepKey(StepKey.SelectBlastType);
        },
        dataTestAttrs: {component: 'SequenceMapBlastModal', element: 'back'},
      },
      {
        text: 'Next',
        submit: true,
        onClick: () => {
          setFilter({
            ...filter,
            value: {
              ...filter.value,
              textSequences: [
                {
                  sequence: selectedResidues,
                  name: selectedResidues.slice(0, 5),
                },
              ],
            },
          });
          setCurrentStepKey(StepKey.SetParameters);
        },
        dataTestAttrs: {component: 'SequenceMapBlastModal', element: 'next'},
      },
    ];
  } else {
    const noValidTextSequences =
      !filter.value.textSequences.length ||
      filter.value.textSequences.every(
        (ts) => !validateBLASTSequence(stripWhitespace(ts.sequence), filter.operator as BLASTOperator).isValid
      );
    const numericParamsInvalid = filter.params?.culling_limit == null || filter.params?.evalue == null;
    return [
      {
        text: 'Back',
        onClick: () => {
          setCurrentStepKey(StepKey.SelectSequences);
        },
        dataTestAttrs: {component: 'SequenceMapBlastModal', element: 'back'},
      },
      {
        text: 'Run BLAST',
        submit: true,
        onClick: () => {
          const searchParams: SearchParams = {
            ...getDefaultSearchSearchParams(),
            sorts: [{key: SortKey.SORT_BLAST_E_VALUE, reverse: false}],
          };
          onRequestSubmit(mergeBLASTFilter(searchParams, filter));
          trackBlastSubmission(filter.operator as BLASTOperator, new Set([BLASTInputSource.Selection]));
        },
        dataTestAttrs: {component: 'SequenceMapBlastModal', element: 'submit'},
        disabled: numericParamsInvalid || noValidTextSequences,
      },
    ];
  }
}

export default function BLASTModalForSelection({
  initialFilter,
  residueSourceId,
  residueType,
  selectedResidues,
  displayStart,
  displayEnd,
  displayLength,
  onRequestSubmit,
  onRequestClose,
}: BLASTModalForSelectionProps): JSX.Element {
  const [currentStepKey, setCurrentStepKey] = useState(StepKey.SelectBlastType);
  const [filter, setFilter] = useState(initialFilter);
  const sequenceType = sequenceTypeMap[residueType];
  const selectionMetadata: Array<{label: string; value: number}> = [
    {
      label: 'Start',
      value: displayStart,
    },
    {
      label: 'End',
      value: displayEnd,
    },
    {
      label: 'Length',
      value: displayLength,
    },
  ];

  const buttons = getStepModalButtons(
    currentStepKey,
    setCurrentStepKey,
    selectedResidues,
    filter,
    setFilter,
    onRequestSubmit
  );

  return (
    <ExpandableModal
      enableRedesign={true}
      title={BLAST_MODAL_TITLE}
      buttons={buttons}
      onRequestClose={() => {
        trackBlastModalClose(BlastModalTrigger.SELECTION, currentStepKey);
        onRequestClose();
      }}
      mediumLarge={true}
      expandHeight={true}
      scrollable={true}
    >
      <div className={css(styles.stepNavMarginAdjustment)}>
        <StepNav<StepKey>
          steps={[
            {
              key: StepKey.SelectBlastType,
              title: 'Select BLAST Type',
              metadata: getSelectBlastTypeStepTitle(currentStepKey, filter.operator),
            },
            {
              key: StepKey.SelectSequences,
              title: 'Select sequences',
              metadata: getSelectSequencesStepTitle(currentStepKey, filter),
            },
            {
              key: StepKey.SetParameters,
              title: 'Set parameters',
            },
          ]}
          currentStepKey={currentStepKey}
          onUpdateCurrentStep={(newStepKey: StepKey): void => {
            setCurrentStepKey(newStepKey);
          }}
          statusByStep={{
            [StepKey.SelectBlastType]: null,
            [StepKey.SelectSequences]:
              currentStepKey === StepKey.SetParameters ? null : StepItemStatus.UNVISITED,
            [StepKey.SetParameters]: StepItemStatus.UNVISITED,
          }}
          size={StepNavSize.LARGE}
        />
      </div>
      <>
        {currentStepKey === StepKey.SelectBlastType && (
          <SelectBlastTypePane filter={filter} setFilter={setFilter} residueType={residueType} />
        )}
        {currentStepKey === StepKey.SelectSequences && (
          <SelectionPane
            sequenceType={sequenceType}
            selectedResidues={selectedResidues}
            residueSourceId={residueSourceId}
            selectionMetadata={selectionMetadata}
          />
        )}
        {currentStepKey === StepKey.SetParameters && (
          <SetParametersPane filter={filter} setFilter={setFilter} />
        )}
      </>
    </ExpandableModal>
  );
}

interface SelectionPaneProps {
  sequenceType: string;
  selectedResidues: string;
  residueSourceId: string;
  selectionMetadata: Array<{label: string; value: number}>;
}

function SelectionPane({
  sequenceType,
  selectedResidues,
  residueSourceId,
  selectionMetadata,
}: SelectionPaneProps): JSX.Element {
  return (
    <>
      <FormGroup>
        <Label className={css(styles.sectionTitle)}>{`${sequenceType} sequence`}</Label>
        <div className={css(styles.sequenceInput)}>
          <TextArea value={selectedResidues} disabled={true} rows={3} />
        </div>
      </FormGroup>
      <FormGroup>
        <div className={css(styles.metadataColumn)}>
          <Label className={css(styles.sectionTitle)}>Source</Label>
          <div className={css(styles.sequenceInput)}>
            <BenchChip key={residueSourceId} id={residueSourceId} size={ChipSize.TABLE_CELL} isLink={false} />
          </div>
        </div>
        {selectionMetadata.map(({label, value}) => (
          <div key={`metadata-${label}`} className={css(styles.metadataColumn)}>
            <Label className={css(styles.sectionTitle)}>{label}</Label>
            <div className={css(styles.sequenceInput)}>
              <TextInput value={value} disabled={true} width="7px" />
            </div>
          </div>
        ))}
      </FormGroup>
    </>
  );
}
